import React, { useCallback } from "react";
import { Marker as MapMarker } from "react-map-gl";
import { MarkerIconStyles } from "../../Map/MapStyles";
import { markerSVGSwitch } from "../../../shared/components/MarkerImages";
import { useEditState } from "../EditContext";
import { useMapData } from "./MapDataContext";
import { EditorMapPano, SelectedLink } from "../../../utils/editorUtils";
import { MapPoi } from "../../../types";
import { useRestAuth } from "../../../hooks/useRestAuth";
import { MapPointLabel } from "../EditorStyles";

interface EditorPanoMarkerProps {
  index: number;
  point: EditorMapPano;
  selectedLink: SelectedLink | undefined;
  setSelectedLink: React.Dispatch<React.SetStateAction<SelectedLink | undefined>>;
  setLinkEditorLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setLinkEditorCoordinatesLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setRefreshingData: React.Dispatch<React.SetStateAction<boolean>>;
  unlinked?: boolean;
}

const EditorPanoMarker: React.FC<EditorPanoMarkerProps> = ({
  index,
  point,
  selectedLink,
  setSelectedLink,
  setLinkEditorLoading,
  setLinkEditorCoordinatesLoading,
  setRefreshingData,
  unlinked = false,
}) => {
  const { mapPanoRepository, target, editData, mapPoiRepository } = useEditState();
  const { updateLayerData, markAllLayersStale } = useMapData();
  const restAuth = useRestAuth();
  const disabled = unlinked || (selectedLink && (selectedLink.type !== 'pano' || selectedLink.item !== point.pano));

  // Helper function to refresh layer data after toggle (only for target layers)
  const refreshLayerData = useCallback(async () => {
    if (!restAuth || !target) return;
    
    setRefreshingData(true);
    
    // For specific layer: fetch all layer data again
    const panoLinks = await mapPanoRepository.get(restAuth, target);
    const poiLinks = await mapPoiRepository.get(restAuth, target);
    const unlinkedPanoLinks = await mapPanoRepository.get_unlinked(restAuth, target);
    const unlinkedPoiLinks = await mapPoiRepository.get_unlinked(restAuth, target);
    const unusedPanoLinks = await mapPanoRepository.get_unused(restAuth);
    const unusedPoiLinks = await mapPoiRepository.get_unused(restAuth);
    
    const refreshedData = {
      panorama_location: panoLinks,
      poi_location: poiLinks,
      unlinked_panorama_location: unlinkedPanoLinks,
      unlinked_poi_location: unlinkedPoiLinks,
      unused_panorama_location: unusedPanoLinks,
      unused_poi_location: unusedPoiLinks,
    };
    
    // Update cache with fresh data
    updateLayerData(target, refreshedData);
    editData<"map">(refreshedData);
    
    setRefreshingData(false);
  }, [restAuth, target, mapPanoRepository, mapPoiRepository, updateLayerData, editData, setRefreshingData]);

  const handleDragEnd = useCallback(
    async (event) => {
      if (restAuth) {
        if (selectedLink?.item !== index) {
          setLinkEditorLoading(true);
          setSelectedLink({ type: "pano", item: point.pano });
        } else {
          setLinkEditorCoordinatesLoading(true);
        }
        const lat = event.lngLat.lat;
        const long = event.lngLat.lng;
        const links = await mapPanoRepository.update(restAuth, { lat, long }, point.pano);
        
        // For global map: update only the panorama_location data
        editData<"map">({ panorama_location: links });
        
        // If this is a global edit (no target), mark all layers as stale
        if (!target) {
          markAllLayersStale();
        }
        
        if (selectedLink?.item !== index) {
          setLinkEditorLoading(false);
        } else {
          setLinkEditorCoordinatesLoading(false);
        }
      }
    },
    [restAuth, selectedLink?.item, index, setLinkEditorLoading, setSelectedLink, point.pano, setLinkEditorCoordinatesLoading, mapPanoRepository, editData, target, markAllLayersStale]
  );  return (
    <MapMarker
      latitude={point.lat}
      longitude={point.long}
      offset={[-18, -18]}
      anchor="top-left"
      draggable={!target}
      onDragEnd={!target ? handleDragEnd : undefined}
      onClick={async () => {
        if (!target) {
          setSelectedLink({ type: "pano", item: point.pano });
        } else {
          if (restAuth) {
            if (unlinked) {
              await mapPanoRepository.create(restAuth, { pano: point.pano }, target);
            } else {
              await mapPanoRepository.delete(restAuth, point.pano, target);
            }
            
            // Refresh layer data na toggle (alleen voor layer-specifieke acties)
            await refreshLayerData();
          }
        }

      }}
    >
      <div style={{ display: "flex" }}>
        <MarkerIconStyles>
          {markerSVGSwitch(
            { type: "pano" } as MapPoi,
            false,
            disabled ? "--color-neutral-50" : point.color,
            disabled ? 0.4 : 1
          )}
        </MarkerIconStyles>
        <MapPointLabel $disabled={disabled} $circle={true}>{point.title}</MapPointLabel>
      </div>
    </MapMarker>
  );
};

export const MemoizedEditorPanoMarker = React.memo(EditorPanoMarker);
